import { createContext, useCallback, useContext, useState } from 'react';
import type {
  DeviceData,
  IssueDescriptionData,
  IssueDetailsData,
  ServiceRequestContextValue,
} from './ServiceContext.types';

export type {
  DeviceData,
  IssueDescriptionData,
  IssueDetailsData,
  ServiceRequestContextValue,
} from './ServiceContext.types';

export const ServiceRequestContext = createContext<ServiceRequestContextValue | undefined>(undefined);

export const useServiceRequestContext = (): ServiceRequestContextValue => {
  const context = useContext(ServiceRequestContext);

  if (!context) {
    throw new Error('useServiceRequestContext must be used within ServiceRequestContext.Provider');
  }

  return context;
};

export const useServiceRequest = (): ServiceRequestContextValue => {
  const [currentServiceStep, setCurrentServiceStep] = useState(0);
  const [currentAddServiceCaseStep, setCurrentAddServiceCaseStep] = useState(0);
  const [emailRequested, setEmailRequested] = useState('');
  const [deviceData, setDeviceData] = useState<DeviceData>({});
  const [issueDescriptionData, setIssueDescriptionData] = useState<IssueDescriptionData>({});
  const [issueDetailsData, setIssueDetailsData] = useState<IssueDetailsData>({});
  const [isServiceRequestSubmitted, setIsServiceRequestSubmitted] = useState(false);

  const handleNavigateToAddServiceCase = useCallback(() => {
    setCurrentServiceStep(2);
  }, []);

  const handleServiceNext = useCallback(() => {
    setCurrentServiceStep(prev => prev + 1);
  }, []);

  const handleServiceBack = useCallback(() => {
    setCurrentServiceStep(prev => prev - 1);
  }, []);

  const handleAddServiceCaseNext = useCallback(() => {
    setCurrentAddServiceCaseStep(prev => prev + 1);
  }, []);

  const handleAddServiceCaseBack = useCallback(() => {
    setCurrentAddServiceCaseStep(prev => prev - 1);
  }, []);

  const handleEmail = useCallback((email: string) => {
    setEmailRequested(email);
  }, []);

  const handleDevice = useCallback((data: DeviceData) => {
    setDeviceData(data);
  }, []);

  const handleIssueDescription = useCallback((data: IssueDescriptionData) => {
    setIssueDescriptionData(data);
  }, []);

  const handleIssueDetails = useCallback((data: IssueDetailsData) => {
    setIssueDetailsData(data);
  }, []);

  const handleSubmitRequest = useCallback(() => {
    // TODO: Replace with actual API call to submit the service request
    // eslint-disable-next-line no-console
    console.log('Submitting service request with data:', {
      email: emailRequested,
      device: deviceData,
      issueDescription: issueDescriptionData,
      issueDetails: issueDetailsData,
    });
    setCurrentAddServiceCaseStep(prev => prev + 1);
    setIsServiceRequestSubmitted(true);
  }, [emailRequested, deviceData, issueDescriptionData, issueDetailsData]);

  return {
    currentServiceStep,
    currentAddServiceCaseStep,
    emailRequested,
    deviceData,
    issueDescriptionData,
    issueDetailsData,
    isServiceRequestSubmitted,
    handleNavigateToAddServiceCase,
    handleServiceNext,
    handleServiceBack,
    handleAddServiceCaseNext,
    handleAddServiceCaseBack,
    handleEmail,
    handleDevice,
    handleIssueDescription,
    handleIssueDetails,
    handleSubmitRequest,
  };
};
